import { inject, injectable } from "tsyringe";
import { map } from "rxjs/operators";
import { DocumentsDownloadRepository } from "src/containers/documents-download/repositories/documents-download/documents-download-repository";
import { DocumentDetail } from "src/containers/documents-download/models/document-detail.model";
import { Document } from "src/containers/documents-download/models/document.model";
import { autoBind } from "src/shared/decorators/autobind";
import { ProductCategories } from "src/constants/product-categories";
import { EpsPlan, PgaPolicy } from "src/shared/models";

@injectable()
export class DocumentsDownloadService {
    constructor(
        @inject("DocumentsDownloadRepository")
        private repository: DocumentsDownloadRepository
    ) {}

    @autoBind
    getEpsDocumentList(plan: EpsPlan): Promise<Array<Document>> {
        return this.repository
            .getEpsDocumentList(plan.insuredCode)
            .pipe(
                map((data) =>
                    data.map((doc) => Document.fromJson<Document>(doc))
                )
            )
            .toPromise();
    }

    @autoBind
    getSoatDocumentList(policy: PgaPolicy): Promise<Array<Document>> {
        return this.repository
            .getSoatDocumentList(policy.number)
            .pipe(
                map((data) =>
                    data.map((doc) => Document.fromJson<Document>(doc))
                )
            )
            .toPromise();
    }

    @autoBind
    getHealthDocumentList(
        policy: PgaPolicy,
        userIdentity: string = window.userIdentity
    ): Promise<Array<Document>> {
        const payload = {
            policyId: policy.id,
            systemCode: policy.product.systemCode,
            userIdentity,
        };
        return this.repository
            .getHealthDocumentList(payload)
            .pipe(
                map((data) =>
                    data.map((doc) => Document.fromJson<Document>(doc))
                )
            )
            .toPromise();
    }

    @autoBind
    getPgaDocumentList(
        policy: PgaPolicy,
        userIdentity: string = window.userIdentity
    ): Promise<Array<Document>> {
        const payload = {
            policyId: policy.id,
            systemCode: policy.product.systemCode,
            userIdentity,
        };
        return this.repository
            .getPgaDocumentList(payload)
            .pipe(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                map((data: Array<Record<string, any>>) =>
                    data.map((file) => Document.fromJson<Document>(file))
                )
            )
            .toPromise();
    }

    @autoBind
    getEpsDocumentDetail(
        plan: EpsPlan,
        userIdentity = window.userIdentity
    ): Promise<DocumentDetail> {
        const payload = {
            planNumber: plan.number,
            contractorId: plan.contractorId,
            userIdentity,
        };
        return this.repository
            .getEpsDocumentDetail(payload)
            .pipe(map((data) => DocumentDetail.fromJson<DocumentDetail>(data)))
            .toPromise();
    }

    @autoBind
    getEpsCertificateDocumentDetail(
        plan: EpsPlan,
        userIdentity: string = window.userIdentity
    ): Promise<DocumentDetail> {
        return this.repository
            .getEpsCertificateDocumentDetail(plan.number, userIdentity)
            .toPromise();
    }

    @autoBind
    getSoatDocumentDetail(
        policy: PgaPolicy,
        document: Document,
        userIdentity: string = window.userIdentity
    ): Promise<DocumentDetail> {
        const payload = {
            policyId: policy.id,
            systemCode: policy.product.systemCode,
            fileId: document.id,
            userIdentity,
        };
        return this.repository
            .getSoatDocumentDetail(payload)
            .pipe(map((data) => DocumentDetail.fromJson<DocumentDetail>(data)))
            .toPromise();
    }

    @autoBind
    getSoatDocumentDetailByEmail(
        policy: PgaPolicy,
        document: Document,
        userIdentity: string = window.userIdentity
    ): Promise<{}> {
        const payload = {
            policyId: policy.id,
            systemCode: policy.product.systemCode,
            fileId: document.id,
            userIdentity,
        };
        return this.repository
            .getSoatDocumentDetailByEmail(payload)
            .toPromise();
    }

    @autoBind
    getHealthDocumentDetail(
        policy: PgaPolicy,
        document: Document,
        userIdentity: string = window.userIdentity
    ): Promise<DocumentDetail> {
        const payload = {
            policyId: policy.id,
            systemCode: policy.product.systemCode,
            fileId: document.id,
            userIdentity,
        };
        return this.repository
            .getHealthDocumentDetail(payload)
            .pipe(map((data) => DocumentDetail.fromJson<DocumentDetail>(data)))
            .toPromise();
    }

    @autoBind
    getPgaDocumentDetail(
        policy: PgaPolicy,
        document: Document,
        userIdentity: string = window.userIdentity
    ): Promise<DocumentDetail> {
        const payload = {
            policyId: policy.id,
            systemCode: policy.product.systemCode,
            fileId: document.id,
            userIdentity,
        };
        return this.repository
            .getPgaDocumentDetail(payload)
            .pipe(map((data) => DocumentDetail.fromJson<DocumentDetail>(data)))
            .toPromise();
    }

    @autoBind
    private allowDocumentDownload(policy: PgaPolicy): boolean {
        // SOAT is now *not* downloadable by accessing this widget,
        // it will have its own section (SNEATL-3050)
        return (
            (policy.product.category !== ProductCategories.SOAT &&
                policy.product.features.downloadPolicies) ??
            false
        );
    }

    // Validates policy's document can be downloaded *through* the Tranversal button at Home
    @autoBind
    getDocumentDownloadByTransversalOption(policies: Array<PgaPolicy>) {
        return policies.filter((policy) => {
            return this.allowDocumentDownload(policy);
        });
    }

    @autoBind
    // Validates access to SoatDownload button at Home
    getDocumentDownloadByOnlySoatOption(policies: Array<PgaPolicy>) {
        return policies.filter((policy) => {
            return policy.product.category === ProductCategories.SOAT;
        });
    }
}
